import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StepIndicator } from '@/components/ui';
import { useAI } from '@/hooks/useAI';
import { useAIBrandProfiles } from '@/hooks/useAIBrandProfiles';
import { useAIBriefingSettings } from '@/hooks/useAIBriefingSettings';
import { useAuth } from '@/hooks/useAuth';
import { devLog } from '@/utils/logger';
import {
  WizardStep,
  AIBriefingFormData,
  DEFAULT_FORM_DATA,
  WIZARD_STEPS,
  formatIncludesImages,
  ValidationErrors,
} from './types';
import { WizardFooter } from './WizardFooter';
import {
  PlatformStep,
  AudienceStep,
  StyleStep,
  ImageStep,
  GeneratingStep,
  ResultStep,
} from './steps';

/**
 * AIBriefingWizard Component
 *
 * Multi-step wizard for creating AI-generated social media content.
 * Replaces the single-modal AIBriefingModal with a guided step-by-step flow.
 *
 * Flow:
 * 1. Platform + Objective (what)
 * 2. Audience + Context (who and why)
 * 3. Style (tone, formality, length, language)
 * 4. Images (conditional - only if format includes images)
 * 5. Generating (loading state)
 * 6. Result (show caption + optional image search)
 */

interface AIBriefingWizardProps {
  onClose: () => void;
  onInsertCaption: (caption: string) => void;
  onInsertImage?: (imageUrl: string, credit: string, downloadUrl: string) => void;
  userName?: string;
}

export const AIBriefingWizard = ({
  onClose,
  onInsertCaption,
  onInsertImage,
  userName = 'there',
}: AIBriefingWizardProps) => {
  const { t } = useTranslation('ai');
  const { user } = useAuth();
  const { generateCaption, isErrorHandled } = useAI();
  // Team's active default brand profile — auto-applied silently (no picker in this surface)
  const { defaultProfile } = useAIBrandProfiles();
  const { getSavedSettings, saveSettings } = useAIBriefingSettings();

  // ============================================================================
  // State
  // ============================================================================

  const [currentStep, setCurrentStep] = useState<WizardStep>('platform');
  const [formData, setFormData] = useState<AIBriefingFormData>(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [generatedCaption, setGeneratedCaption] = useState('');

  // ============================================================================
  // Load saved settings on mount
  // ============================================================================

  useEffect(() => {
    if (!user?.teamId) return;

    devLog.log('[AIBriefingWizard] Loading saved settings for team', user.teamId);
    const savedSettings = getSavedSettings(user.teamId);

    // Pre-fill form with saved preferences (keep per-post fields empty)
    setFormData((prev) => ({
      ...prev,
      tone: savedSettings.tone,
      formality: savedSettings.formality,
      length: savedSettings.length as AIBriefingFormData['length'],
      language: savedSettings.language,
      contentFormat: savedSettings.contentFormat as AIBriefingFormData['contentFormat'],
      wantImageSuggestion: savedSettings.wantImageSuggestion,
      // Don't pre-fill: platform, objective, audience, context, imageDescription
    }));
  }, [user?.teamId, getSavedSettings]);

  // ============================================================================
  // Handlers
  // ============================================================================

  /**
   * Update form field and clear related errors
   */
  const handleFieldChange = useCallback(
    <K extends keyof AIBriefingFormData>(
      field: K,
      value: AIBriefingFormData[K]
    ) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear error for this field
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }

      // Sync image suggestion with content format
      if (field === 'contentFormat') {
        if (value === 'text') {
          setFormData((prev) => ({
            ...prev,
            wantImageSuggestion: false,
            imageDescription: '',
          }));
        } else {
          // image_text, video_text, carousel_text → default to wanting images
          setFormData((prev) => ({
            ...prev,
            wantImageSuggestion: true,
          }));
        }
      }
    },
    [errors]
  );

  /**
   * Validate current step
   */
  const validateStep = useCallback(
    (step: WizardStep): boolean => {
      const newErrors: ValidationErrors = {};

      switch (step) {
        case 'platform':
          if (!formData.platform) {
            newErrors.platform = t('ai_briefing_error_no_platform');
          }
          if (!formData.objective.trim()) {
            newErrors.objective = t('ai_briefing_error_no_objective');
          }
          break;

        case 'audience':
          // Optional fields, no validation needed
          break;

        case 'style':
          if (!formData.language.trim()) {
            newErrors.language = t('ai_wizard_error_no_language');
          }
          break;

        case 'images':
          // If user wants image suggestions but hasn't provided description
          if (
            formData.wantImageSuggestion &&
            !formData.imageDescription.trim()
          ) {
            newErrors.imageDescription = t('ai_wizard_error_no_image_description');
          }
          break;

        default:
          break;
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [formData, t]
  );

  /**
   * Navigate to next step (with validation)
   */
  const handleNext = useCallback(() => {
    if (!validateStep(currentStep)) {
      return;
    }

    const steps: WizardStep[] = ['platform', 'audience', 'style'];

    // Add images step if format includes images
    if (formatIncludesImages(formData.contentFormat)) {
      steps.push('images');
    }

    const currentIndex = steps.indexOf(currentStep);
    const nextStep = steps[currentIndex + 1];

    if (nextStep) {
      setCurrentStep(nextStep);
    }
  }, [currentStep, formData.contentFormat, validateStep]);

  /**
   * Navigate to previous step
   */
  const handleBack = useCallback(() => {
    const steps: WizardStep[] = ['platform', 'audience', 'style'];

    // Add images step if format includes images
    if (formatIncludesImages(formData.contentFormat)) {
      steps.push('images');
    }

    const currentIndex = steps.indexOf(currentStep);
    const prevStep = steps[currentIndex - 1];

    if (prevStep) {
      setCurrentStep(prevStep);
    }
  }, [currentStep, formData.contentFormat]);

  /**
   * Generate AI caption
   */
  const handleGenerate = useCallback(async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setCurrentStep('generating');

    try {
      const response = await generateCaption({
        topic: formData.objective,
        tone: mapToneToAPI(formData.tone),
        length: formData.length,
        platform: formData.platform,
        brandProfileId: defaultProfile?.id,
      });

      if (response) {
        setGeneratedCaption(response.content);
        setCurrentStep('result');

        // Save preferences after successful generation
        if (user?.teamId) {
          devLog.log('[AIBriefingWizard] Saving preferences for team', user.teamId);
          saveSettings(user.teamId, {
            tone: formData.tone,
            formality: formData.formality,
            length: formData.length,
            language: formData.language,
            contentFormat: formData.contentFormat,
            wantImageSuggestion: formData.wantImageSuggestion,
          });
        }

      } else {
        // Generation failed - show error and go back to previous step
        devLog.error('[AIBriefingWizard] Caption generation failed');
        setCurrentStep('style');
        if (!isErrorHandled()) setErrors({ objective: t('ai_briefing_error_message') });
      }
    } catch (error) {
      devLog.error('[AIBriefingWizard] Caption generation error:', error);
      setCurrentStep('style');
      setErrors({ objective: t('ai_briefing_error_message') });
    }
  }, [
    currentStep,
    formData,
    generateCaption,
    isErrorHandled,
    defaultProfile,
    saveSettings,
    user?.teamId,
    validateStep,
    t,
  ]);

  /**
   * User selects an image from search results
   * Only adds the image to media - doesn't insert caption or close modal
   * User must click "Add to Caption" to confirm and close
   */
  const handleSelectImage = useCallback(
    (image: { url: string; photographer: string; downloadUrl: string }) => {
      if (onInsertImage) {
        onInsertImage(
          image.url,
          `${t('ai_briefing_image_photo_by')} ${image.photographer}`,
          image.downloadUrl
        );
      }
      // Don't close modal - user needs to confirm caption separately
    },
    [onInsertImage, t]
  );

  /**
   * Add generated caption to composer
   */
  const handleAddToCaption = useCallback(() => {
    if (generatedCaption) {
      onInsertCaption(generatedCaption);
      onClose();
    }
  }, [generatedCaption, onInsertCaption, onClose]);

  /**
   * Start over (keep form data for tweaking)
   */
  const handleTweak = useCallback(() => {
    setCurrentStep('platform');
    setGeneratedCaption('');
    setErrors({});
  }, []);

  /**
   * Full reset
   */
  const resetWizard = useCallback(() => {
    setCurrentStep('platform');
    setFormData(DEFAULT_FORM_DATA);
    setErrors({});
    setGeneratedCaption('');
  }, []);

  /**
   * Handle close (with reset)
   */
  const handleClose = useCallback(() => {
    resetWizard();
    onClose();
  }, [resetWizard, onClose]);

  // ============================================================================
  // Step navigation helpers
  // ============================================================================

  /**
   * Get visible steps (filter out images step if not applicable)
   */
  const getVisibleSteps = useCallback(() => {
    return WIZARD_STEPS.filter((step) => {
      if (step.id === 'images') {
        return formatIncludesImages(formData.contentFormat);
      }
      return true;
    }).map((step) => ({
      id: step.id,
      label: t(step.label),
    }));
  }, [formData.contentFormat, t]);

  /**
   * Get current step index in visible steps
   */
  const getStepIndex = useCallback(() => {
    const visibleSteps = getVisibleSteps();
    return visibleSteps.findIndex((step) => step.id === currentStep);
  }, [currentStep, getVisibleSteps]);

  /**
   * Handle step indicator click (only allow clicking completed steps)
   */
  const handleStepClick = useCallback(
    (stepIndex: number) => {
      const visibleSteps = getVisibleSteps();
      const targetStep = visibleSteps[stepIndex];
      const currentIndex = getStepIndex();

      // Only allow navigating to completed steps (before current)
      if (stepIndex < currentIndex && targetStep) {
        setCurrentStep(targetStep.id as WizardStep);
      }
    },
    [getVisibleSteps, getStepIndex]
  );

  /**
   * Check if we can go back
   */
  const canGoBack = useCallback(() => {
    const steps: WizardStep[] = ['platform', 'audience', 'style'];
    if (formatIncludesImages(formData.contentFormat)) {
      steps.push('images');
    }
    return steps.indexOf(currentStep) > 0;
  }, [currentStep, formData.contentFormat]);

  /**
   * Check if we can go forward
   */
  const canGoNext = useCallback(() => {
    // Basic validation for current step
    switch (currentStep) {
      case 'platform':
        return !!formData.platform && !!formData.objective.trim();
      case 'audience':
        return true; // Optional fields
      case 'style':
        return !!formData.language.trim();
      case 'images':
        return !formData.wantImageSuggestion || !!formData.imageDescription.trim();
      default:
        return false;
    }
  }, [currentStep, formData]);

  /**
   * Check if we're on the last form step
   */
  const isLastFormStep = useCallback(() => {
    const steps: WizardStep[] = ['platform', 'audience', 'style'];
    if (formatIncludesImages(formData.contentFormat)) {
      steps.push('images');
    }
    return currentStep === steps[steps.length - 1];
  }, [currentStep, formData.contentFormat]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="flex flex-col h-full max-h-[70vh]">
      {/* Header with greeting - only on form steps */}
      {!['generating', 'result'].includes(currentStep) && (
        <div className="flex-shrink-0 text-center mb-4">
          <p className="text-sm text-gray-500">
            {t('ai_briefing_greeting', { userName })}
          </p>
        </div>
      )}

      {/* Step Indicator - only during form steps */}
      {!['generating', 'result'].includes(currentStep) && (
        <StepIndicator
          steps={getVisibleSteps()}
          currentStepIndex={getStepIndex()}
          onStepClick={handleStepClick}
          className="mb-4"
        />
      )}

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto">
        {currentStep === 'platform' && (
          <PlatformStep
            formData={formData}
            onFieldChange={handleFieldChange}
            errors={errors}
          />
        )}
        {currentStep === 'audience' && (
          <AudienceStep
            formData={formData}
            onFieldChange={handleFieldChange}
            errors={errors}
          />
        )}
        {currentStep === 'style' && (
          <StyleStep
            formData={formData}
            onFieldChange={handleFieldChange}
            errors={errors}
          />
        )}
        {currentStep === 'images' && (
          <ImageStep
            formData={formData}
            onFieldChange={handleFieldChange}
            errors={errors}
          />
        )}
        {currentStep === 'generating' && <GeneratingStep />}
        {currentStep === 'result' && (
          <ResultStep
            generatedCaption={generatedCaption}
            showImageSearch={
              formatIncludesImages(formData.contentFormat) &&
              formData.wantImageSuggestion
            }
            initialImageQuery={formData.imageDescription}
            onSelectImage={handleSelectImage}
          />
        )}
      </div>

      {/* Footer */}
      <WizardFooter
        currentStep={currentStep}
        canGoBack={canGoBack()}
        canGoNext={canGoNext()}
        isLastFormStep={isLastFormStep()}
        isGenerating={currentStep === 'generating'}
        showResult={currentStep === 'result'}
        onBack={handleBack}
        onNext={handleNext}
        onCancel={handleClose}
        onGenerate={handleGenerate}
        onAddToCaption={handleAddToCaption}
        onTweak={handleTweak}
      />
    </div>
  );
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Map detailed tone values to simplified API tones
 */
function mapToneToAPI(
  tone: string
): 'professional' | 'casual' | 'funny' | 'formal' | 'friendly' {
  const toneMap: Record<
    string,
    'professional' | 'casual' | 'funny' | 'formal' | 'friendly'
  > = {
    friendly_professional: 'friendly',
    authoritative_informative: 'professional',
    urgent_persuasive: 'professional',
    casual_conversational: 'casual',
    professional_trustworthy: 'professional',
    humorous_informal: 'funny',
    professional_straightforward: 'professional',
    serious_empathetic: 'formal',
    positive_enthusiastic: 'friendly',
    authoritative_professional: 'professional',
    casual_funny: 'funny',
    authoritative_expert: 'professional',
  };

  return toneMap[tone] || 'professional';
}
